import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Clock, DollarSign, Users, Briefcase, Heart, Coffee, Zap, X, Upload } from 'lucide-react';
import LogoPng from '../../assets/InitX_Logo.png';
import SEO from '../../components/SEO';

const CareersContainer = styled.div`
  padding: 0;
  background: linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%);
  min-height: 100vh;
`;

const Hero = styled.section`
  text-align: center;
  padding: 80px 20px;
  background: linear-gradient(135deg, #ffffff 0%, #fb923c 50%, #f97316 100%);
  color: #333;
  margin: 0 -20px 60px;
  border-radius: 0 0 20px 20px;
  
  @media (max-width: 768px) {
    padding: 60px 15px;
    margin: 0 -15px 40px;
  }
  
  @media (max-width: 480px) {
    padding: 40px 10px;
    margin: 0 -10px 30px;
  }
`;

const BenefitsSection = styled.section`
  margin-bottom: 80px;
  padding: 0 20px;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    margin-bottom: 60px;
    padding: 0 15px;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 40px;
    padding: 0 10px;
  }
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;
  margin-top: 40px;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
    margin-top: 30px;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 15px;
    margin-top: 20px;
  }
`;

const BenefitCard = styled(motion.div)`
  background: white;
  padding: 30px;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  text-align: center;
`;

const BenefitIcon = styled.div`
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #fb923c 0%, #f97316 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  color: white;
`;

const JobsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 30px;
  margin-top: 40px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
    margin-top: 30px;
  }
  
  @media (max-width: 480px) {
    gap: 15px;
    margin-top: 20px;
  }
`;

const JobsSection = styled.section`
  margin-bottom: 80px;
  padding: 0 20px;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    margin-bottom: 60px;
    padding: 0 15px;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 40px;
    padding: 0 10px;
  }
`;

const JobCard = styled(motion.div)`
  background: linear-gradient(135deg, #ffffff 0%, #fafbfc 100%);
  padding: 20px;
  border-radius: 16px;
  box-shadow: 0 8px 25px rgba(0,0,0,0.08);
  transition: all 0.3s ease;
  min-height: 380px;
  display: flex;
  flex-direction: column;
  position: relative;
  border: 1px solid #e2e8f0;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 20px 40px rgba(251, 146, 60, 0.15);
    border-color: rgba(251, 146, 60, 0.2);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #fb923c 0%, #f97316 100%);
  }
`;

const JobHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex: 1;
`;

const JobInfo = styled.div`
  flex: 1;
`;

const ButtonContainer = styled.div`
  margin-top: auto;
  padding-top: 20px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const JobMeta = styled.div`
  display: flex;
  gap: 20px;
  margin: 15px 0;
  flex-wrap: wrap;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  color: #666;
  font-size: 14px;
`;

const JobTags = styled.div`
  display: flex;
  gap: 10px;
  margin: 15px 0;
  flex-wrap: wrap;
`;

const Tag = styled.span`
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  color: #475569;
  padding: 5px 12px;
  border-radius: 15px;
  font-size: 11px;
  font-weight: 500;
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
  
  &:hover {
    background: linear-gradient(135deg, #fb923c 0%, #f97316 100%);
    color: white;
    transform: scale(1.05);
  }
`;

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
`;

const ModalContent = styled(motion.div)`
  background: white;
  border-radius: 20px;
  padding: 40px;
  max-width: 600px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #333;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  
  &:focus {
    outline: none;
    border-color: #fb923c;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  min-height: 100px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #fb923c;
  }
`;

const FileInput = styled.input`
  width: 100%;
  padding: 12px;
  border: 2px dashed #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #fb923c;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 15px;
  background: linear-gradient(135deg, #fb923c 0%, #f97316 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-2px);
  }
`;

const ApplyButton = styled.button`
  background: linear-gradient(135deg, #fb923c 0%, #f97316 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: auto;
  align-self: flex-end;
  font-size: 13px;
  box-shadow: 0 4px 12px rgba(251, 146, 60, 0.25);
  
  &:hover {
    transform: translateY(-2px) scale(1.05);
    box-shadow: 0 6px 20px rgba(251, 146, 60, 0.4);
  }
`;

const Careers = () => {
  const [selectedDepartment, setSelectedDepartment] = useState('All');
  const [showModal, setShowModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [applicationId, setApplicationId] = useState('');
  const [uploadStatus, setUploadStatus] = useState('idle'); // idle, uploading, success
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    experience: '',
    education: '',
    currentCompany: '',
    expectedSalary: '',
    availableFrom: '',
    coverLetter: '',
    portfolio: '',
    skills: [],
    primaryTech: '',
    workType: '',
    resume: null
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadStatus('uploading');
      // Simulate upload process
      setTimeout(() => {
        setFormData({
          ...formData,
          resume: file
        });
        setUploadStatus('success');
      }, 1500);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const appId = 'INITX-' + Date.now().toString().slice(-6);
    setApplicationId(appId);

    const whatsappMessage = `Hi InitX Technologies!

New Job Application:

*Position:* ${selectedJob?.title}
*Department:* ${selectedJob?.department}
*Application ID:* ${appId}

*Name:* ${formData.name}
*Email:* ${formData.email}
*Phone:* ${formData.phone}
*Experience:* ${formData.experience}
*Education:* ${formData.education}
*Current Company:* ${formData.currentCompany}
*Expected Salary:* ${formData.expectedSalary}
*Available From:* ${formData.availableFrom}
*Skills:* ${formData.skills.join(', ')}
*Cover Letter:* ${formData.coverLetter}

Please review my application. Thank you!`;

    const whatsappURL = `https://wa.me/918986283243?text=${encodeURIComponent(whatsappMessage)}`;

    setShowModal(false);
    setShowSuccessModal(true);
    setFormData({
      name: '', email: '', phone: '', experience: '', education: '',
      currentCompany: '', expectedSalary: '', availableFrom: '',
      coverLetter: '', portfolio: '', resume: null
    });
    window.open(whatsappURL, '_blank');
  };

  const handleSkillsChange = (skill) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.includes(skill)
        ? prev.skills.filter(s => s !== skill)
        : [...prev.skills, skill]
    }));
  };

  const getJobSpecificSkills = (job) => {
    const skillSets = {
      'Senior UI/UX Designer': ['Figma', 'Adobe XD', 'Sketch', 'Photoshop', 'Illustrator', 'Prototyping', 'User Research', 'Wireframing'],
      'Frontend Developer': ['JavaScript', 'React', 'HTML/CSS', 'TypeScript', 'Vue.js', 'Angular', 'Sass/SCSS', 'Webpack'],
      'Product Manager': ['Product Strategy', 'Analytics', 'Roadmapping', 'Agile/Scrum', 'User Stories', 'Market Research', 'A/B Testing', 'Jira'],
      'Marketing Specialist': ['Digital Marketing', 'SEO/SEM', 'Google Analytics', 'Social Media', 'Content Marketing', 'Email Marketing', 'PPC', 'Copywriting'],
      'Backend Developer': ['Node.js', 'Python', 'Java', 'Express.js', 'MongoDB', 'PostgreSQL', 'REST APIs', 'Docker']
    };
    return skillSets[job.title] || [];
  };

  const getBranchInfo = (job) => {
    const branches = {
      'Senior UI/UX Designer': 'Design & Creative',
      'Frontend Developer': 'Software Engineering',
      'Product Manager': 'Product & Strategy',
      'Marketing Specialist': 'Marketing & Growth',
      'Backend Developer': 'Software Engineering'
    };
    return branches[job.title] || 'General';
  };

  const openApplication = (job) => {
    // Create URL with job data
    const jobData = encodeURIComponent(JSON.stringify(job));
    const applicationUrl = `/apply?job=${jobData}`;
    window.open(applicationUrl, '_blank');
  };

  const benefits = [
    {
      icon: <Heart size={24} />,
      title: "Health & Wellness",
      description: "Comprehensive health insurance and wellness programs"
    },
    {
      icon: <Coffee size={24} />,
      title: "Work-Life Balance",
      description: "Flexible hours and remote work options"
    },
    {
      icon: <Zap size={24} />,
      title: "Growth Opportunities",
      description: "Continuous learning and career development"
    },
    {
      icon: <Users size={24} />,
      title: "Great Team",
      description: "Work with talented and passionate professionals"
    }
  ];

  const jobs = [
    {
      id: 1,
      title: "Senior Full Stack Developer",
      department: "Engineering",
      location: "Vadodara, Gujarat",
      type: "Full-time",
      salary: "₹15-25 LPA",
      description: "We're looking for an experienced Full Stack Developer to join our engineering team and work on cutting-edge web applications for Fortune 500 clients.",
      requirements: ["5+ years experience in React, Node.js, and MongoDB", "Strong knowledge of cloud platforms (AWS/Azure)", "Experience with microservices architecture", "Excellent problem-solving and communication skills"],
      tags: ["React", "Node.js", "MongoDB", "AWS", "Docker", "Kubernetes"],
      minQualifications: {
        tenth: 70,
        twelfth: 75,
        bachelor: 80
      }
    },
    {
      id: 2,
      title: "AI/ML Engineer",
      department: "Engineering",
      location: "Bangalore, Karnataka",
      type: "Full-time",
      salary: "₹18-30 LPA",
      description: "Join our AI team to develop machine learning models and AI solutions that power next-generation business applications.",
      requirements: ["Strong background in Machine Learning and Deep Learning", "Experience with Python, TensorFlow, PyTorch", "Knowledge of computer vision and NLP", "PhD/Masters in Computer Science or related field preferred"],
      tags: ["Python", "TensorFlow", "PyTorch", "Computer Vision", "NLP", "AWS"],
      minQualifications: {
        tenth: 75,
        twelfth: 80,
        bachelor: 85
      }
    },
    {
      id: 3,
      title: "DevOps Engineer",
      department: "Engineering",
      location: "Mumbai, Maharashtra",
      type: "Full-time",
      salary: "₹12-20 LPA",
      description: "Lead our DevOps initiatives and help scale our infrastructure to support millions of users across global markets.",
      requirements: ["Experience with CI/CD pipelines and automation", "Strong knowledge of Docker, Kubernetes, and cloud platforms", "Experience with monitoring and logging tools", "Scripting skills in Python/Bash"],
      tags: ["Docker", "Kubernetes", "AWS", "Jenkins", "Terraform", "Python"],
      minQualifications: {
        tenth: 70,
        twelfth: 75,
        bachelor: 80
      }
    },
    {
      id: 4,
      title: "Product Manager",
      department: "Product",
      location: "Vadodara, Gujarat",
      type: "Full-time",
      salary: "₹20-35 LPA",
      description: "Drive product strategy and roadmap for our enterprise AI platform, working closely with engineering and design teams.",
      requirements: ["6+ years of product management experience in tech", "Experience with B2B/Enterprise products", "Strong analytical and strategic thinking skills", "MBA or equivalent experience preferred"],
      tags: ["Product Strategy", "Analytics", "Agile", "User Research", "Roadmapping"],
      minQualifications: {
        tenth: 75,
        twelfth: 80,
        bachelor: 85
      }
    },
    {
      id: 5,
      title: "UI/UX Designer",
      department: "Design",
      location: "Remote",
      type: "Full-time",
      salary: "₹8-15 LPA",
      description: "Create beautiful and intuitive user experiences for our web and mobile applications used by millions of users worldwide.",
      requirements: ["3+ years of UI/UX design experience", "Proficiency in Figma, Sketch, or similar tools", "Strong portfolio demonstrating design thinking", "Experience with design systems and prototyping"],
      tags: ["Figma", "Sketch", "Prototyping", "Design Systems", "User Research"],
      minQualifications: {
        tenth: 60,
        twelfth: 65,
        bachelor: 70
      }
    },
    {
      id: 6,
      title: "Business Development Manager",
      department: "Sales",
      location: "Mumbai, Maharashtra",
      type: "Full-time",
      salary: "₹15-25 LPA + Incentives",
      description: "Drive business growth by identifying new opportunities and building relationships with enterprise clients across various industries.",
      requirements: ["5+ years of B2B sales experience in technology", "Experience selling to enterprise clients", "Strong communication and negotiation skills", "Track record of meeting/exceeding sales targets"],
      tags: ["B2B Sales", "Enterprise Sales", "CRM", "Negotiation", "Relationship Building"],
      minQualifications: {
        tenth: 60,
        twelfth: 65,
        bachelor: 70
      }
    },
    {
      id: 7,
      title: "Frontend Development Intern",
      department: "Internship",
      location: "Remote",
      type: "Internship · 3-6 Months",
      salary: "₹5,000 - ₹10,000/month",
      description: "Kickstart your career by working on real-world web projects using React.js. You'll collaborate with our senior developers, contribute to live products, and gain hands-on experience in modern frontend development.",
      requirements: ["Basic knowledge of HTML, CSS, and JavaScript", "Familiarity with React.js is a plus", "Pursuing B.Tech/BCA/MCA or equivalent", "Eagerness to learn and strong problem-solving attitude"],
      tags: ["React", "HTML", "CSS", "JavaScript", "Git", "Responsive Design"],
      minQualifications: {
        tenth: 55,
        twelfth: 60,
        bachelor: 60
      }
    },
    {
      id: 8,
      title: "Architectural Design Intern",
      department: "Internship",
      location: "Sasaram, Bihar / Remote",
      type: "Internship · 3-6 Months",
      salary: "₹4,000 - ₹8,000/month",
      description: "Join our architectural design division and work on real residential and commercial projects. You'll assist in creating 2D/3D drawings, site plans, and structural layouts under the guidance of experienced civil engineers.",
      requirements: ["Pursuing B.Tech Civil Engineering, B.Arch or Diploma in Civil/Architecture", "Basic knowledge of AutoCAD or similar tools", "Understanding of structural and architectural design principles", "Attention to detail and creative thinking"],
      tags: ["AutoCAD", "Civil Design", "Architecture", "2D/3D Drawing", "Structural Planning"],
      minQualifications: {
        tenth: 55,
        twelfth: 60,
        bachelor: 60
      }
    },
    {
      id: 9,
      title: "Digital Marketing Intern",
      department: "Internship",
      location: "Remote",
      type: "Internship · 2-3 Months",
      salary: "₹3,000 - ₹6,000/month",
      description: "Learn and execute digital marketing strategies including SEO, social media management, and content creation. Work directly with our marketing team to grow InitX Technologies' online presence.",
      requirements: ["Interest in digital marketing and social media", "Good written and verbal communication skills", "Basic knowledge of SEO and content writing", "Pursuing any graduation degree"],
      tags: ["SEO", "Social Media", "Content Writing", "Google Analytics", "Canva"],
      minQualifications: {
        tenth: 55,
        twelfth: 60,
        bachelor: 55
      }
    }
  ];

  const departments = ['All', 'Design', 'Engineering', 'Internship', 'Product', 'Sales'];

  const filteredJobs = selectedDepartment === 'All' 
    ? jobs 
    : jobs.filter(job => job.department === selectedDepartment);

  return (
    <CareersContainer>
      <SEO
        title="Careers | InitX Technologies"
        description="Join InitX Technologies and build the future of AI, cloud, and software engineering. Explore open roles and grow with us."
        keywords="careers, jobs, software engineer, AI developer, cloud engineer, tech jobs, InitX Technologies"
        type="website"
        siteName="InitX Technologies"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Careers', url: '/careers' }
        ]}
      />
      <Hero>
        <motion.h1 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          style={{ fontSize: 'clamp(2rem, 5vw, 3rem)', marginBottom: '20px' }}
        >
          Join Our Team
        </motion.h1>
        <motion.p 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          style={{ fontSize: '1.2rem', opacity: 0.9 }}
        >
          Build the future of design with passionate professionals
        </motion.p>
      </Hero>

      <BenefitsSection>
        <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 2.5rem)', textAlign: 'center', marginBottom: '20px', color: '#ffffff', textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>
          Why Work With Us?
        </h2>
        <p style={{ textAlign: 'center', fontSize: '1.2rem', color: '#ffffff', opacity: '0.9', marginBottom: '40px' }}>
          We offer more than just a job - we provide a platform for growth and innovation
        </p>
        
        <BenefitsGrid>
          {benefits.map((benefit, index) => (
            <BenefitCard
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <BenefitIcon>{benefit.icon}</BenefitIcon>
              <h3 style={{ marginBottom: '15px', color: '#333' }}>{benefit.title}</h3>
              <p style={{ color: '#666', lineHeight: '1.6' }}>{benefit.description}</p>
            </BenefitCard>
          ))}
        </BenefitsGrid>
      </BenefitsSection>

      <JobsSection>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '40px', flexWrap: 'wrap', gap: '20px' }}>
          <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 2.5rem)', color: '#ffffff', textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>Open Positions</h2>
          
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {departments.map(dept => (
              <button
                key={dept}
                onClick={() => setSelectedDepartment(dept)}
                style={{
                  padding: '8px 16px',
                  border: selectedDepartment === dept ? 'none' : '2px solid #e2e8f0',
                  background: selectedDepartment === dept ? 'linear-gradient(135deg, #fb923c 0%, #f97316 100%)' : 'white',
                  color: selectedDepartment === dept ? 'white' : '#666',
                  borderRadius: '20px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  transition: 'all 0.3s ease'
                }}
              >
                {dept}
              </button>
            ))}
          </div>
        </div>

        <JobsGrid>
          {filteredJobs.map((job, index) => (
            <JobCard
              key={job.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <JobInfo>
                <h3 style={{ fontSize: '1.4rem', marginBottom: '10px', color: '#1e293b', fontWeight: '700', position: 'relative' }}>
                  {job.title}
                  <div style={{ position: 'absolute', bottom: '-2px', left: '0', width: '30px', height: '2px', background: 'linear-gradient(90deg, #fb923c 0%, #f97316 100%)', borderRadius: '1px' }}></div>
                </h3>
                
                <JobMeta>
                  <MetaItem>
                    <MapPin size={16} />
                    {job.location}
                  </MetaItem>
                  <MetaItem>
                    <Clock size={16} />
                    {job.type}
                  </MetaItem>
                  <MetaItem>
                    <DollarSign size={16} />
                    {job.salary}
                  </MetaItem>
                  <MetaItem>
                    <Briefcase size={16} />
                    {job.department}
                  </MetaItem>
                </JobMeta>
                
                <p style={{ color: '#666', lineHeight: '1.6', marginBottom: '15px' }}>
                  {job.description}
                </p>
                
                <div style={{ marginBottom: '15px' }}>
                  <strong style={{ color: '#333', fontSize: '14px' }}>Requirements:</strong>
                  <ul style={{ marginTop: '8px', paddingLeft: '20px', color: '#666' }}>
                    {job.requirements.map((req, i) => (
                      <li key={i} style={{ marginBottom: '4px' }}>{req}</li>
                    ))}
                  </ul>
                </div>
                
                <JobTags>
                  {job.tags.map((tag, i) => (
                    <Tag key={i}>{tag}</Tag>
                  ))}
                </JobTags>
              </JobInfo>
              
              <ButtonContainer>
                <ApplyButton onClick={() => openApplication(job)}>
                  Apply Now
                </ApplyButton>
              </ButtonContainer>
            </JobCard>
          ))}
        </JobsGrid>
      </JobsSection>

      <AnimatePresence>
        {showModal && selectedJob && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowModal(false)}
          >
            <ModalContent
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                border: '3px solid #fb923c',
                boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(251, 146, 60, 0.1)'
              }}
            >
              <div style={{ position: 'absolute', top: '0', left: '0', right: '0', height: '6px', background: 'linear-gradient(90deg, #fb923c 0%, #f97316 50%, #fb923c 100%)', borderRadius: '20px 20px 0 0' }}></div>
              
              <CloseButton onClick={() => setShowModal(false)}>
                <X size={24} />
              </CloseButton>
              
              <div style={{ textAlign: 'center', marginBottom: '30px', paddingBottom: '20px', borderBottom: '2px solid #e2e8f0' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '10px', marginBottom: '15px' }}>
                  <img src={LogoPng} alt="InitX" style={{ height: '50px', width: 'auto' }} />
                  <span style={{ color: '#1e293b', fontSize: '24px', fontWeight: '700', fontFamily: 'Inter, sans-serif' }}>InitX</span>
                </div>
                <h2 style={{ marginBottom: '8px', color: '#1e293b', fontSize: '28px', fontWeight: '700' }}>Official Job Application</h2>
                <div style={{ background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)', padding: '12px 20px', borderRadius: '12px', marginBottom: '15px', border: '1px solid #0ea5e9' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
                    <div>
                      <h3 style={{ marginBottom: '2px', color: '#fb923c', fontSize: '20px', fontWeight: '600' }}>{selectedJob.title}</h3>
                      <p style={{ marginBottom: '0', color: '#64748b', fontSize: '14px' }}>{selectedJob.department} • {selectedJob.location} • {selectedJob.salary}</p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ background: '#0ea5e9', color: 'white', padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>
                        🏢 {getBranchInfo(selectedJob)}
                      </div>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: '#f0fdf4', padding: '4px 8px', borderRadius: '15px', border: '1px solid #bbf7d0' }}>
                        <div style={{ width: '6px', height: '6px', background: '#22c55e', borderRadius: '50%' }}></div>
                        <span style={{ color: '#15803d', fontSize: '11px', fontWeight: '500' }}>Active Hiring</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div style={{ background: '#f1f5f9', padding: '20px', borderRadius: '12px', marginBottom: '30px', border: '1px solid #e2e8f0' }}>
                <h4 style={{ color: '#1e293b', marginBottom: '15px', fontSize: '18px', fontWeight: '600' }}>📋 Application Requirements</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', fontSize: '14px' }}>
                  <div>
                    <p style={{ color: '#475569', marginBottom: '10px' }}><strong>Required Documents:</strong></p>
                    <ul style={{ color: '#64748b', paddingLeft: '20px', lineHeight: '1.6' }}>
                      <li>Updated Resume/CV (PDF format preferred)</li>
                      <li>Cover Letter explaining your interest</li>
                      <li>Portfolio/Work samples (if applicable)</li>
                    </ul>
                  </div>
                  <div>
                    <p style={{ color: '#475569', marginBottom: '10px' }}><strong>Minimum Qualifications:</strong></p>
                    <ul style={{ color: '#64748b', paddingLeft: '20px', lineHeight: '1.6' }}>
                      {selectedJob.requirements.map((req, i) => (
                        <li key={i}>{req}</li>
                      ))}
                    </ul>
                  </div>
                </div>
                
                <div style={{ marginTop: '20px', borderTop: '1px solid #e2e8f0', paddingTop: '20px' }}>
                  <h5 style={{ color: '#1e293b', marginBottom: '15px', fontSize: '16px', fontWeight: '600' }}>🔄 Selection Process</h5>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ width: '30px', height: '30px', background: '#fb923c', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '12px', fontWeight: 'bold' }}>1</div>
                      <span style={{ color: '#475569', fontSize: '13px' }}>Application Review</span>
                    </div>
                    <div style={{ flex: '1', height: '2px', background: '#e2e8f0', margin: '0 10px' }}></div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ width: '30px', height: '30px', background: '#fb923c', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '12px', fontWeight: 'bold' }}>2</div>
                      <span style={{ color: '#475569', fontSize: '13px' }}>Phone/Video Screening</span>
                    </div>
                    <div style={{ flex: '1', height: '2px', background: '#e2e8f0', margin: '0 10px' }}></div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ width: '30px', height: '30px', background: '#fb923c', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '12px', fontWeight: 'bold' }}>3</div>
                      <span style={{ color: '#475569', fontSize: '13px' }}>Technical Interview</span>
                    </div>
                    <div style={{ flex: '1', height: '2px', background: '#e2e8f0', margin: '0 10px' }}></div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ width: '30px', height: '30px', background: '#10b981', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '12px', fontWeight: 'bold' }}>4</div>
                      <span style={{ color: '#475569', fontSize: '13px' }}>Final Interview</span>
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', fontSize: '12px', color: '#64748b' }}>
                    <div style={{ textAlign: 'center' }}>2-3 days</div>
                    <div style={{ textAlign: 'center' }}>30 mins</div>
                    <div style={{ textAlign: 'center' }}>1-2 hours</div>
                    <div style={{ textAlign: 'center' }}>45 mins</div>
                  </div>
                </div>
                
                <div style={{ marginTop: '15px', padding: '12px', background: '#dbeafe', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
                  <p style={{ color: '#1e40af', fontSize: '13px', margin: '0' }}>
                    💡 <strong>Tip:</strong> Complete applications are reviewed within 48 hours. We'll keep you updated at every step of the process.
                  </p>
                </div>
              </div>
              
              <form onSubmit={handleSubmit}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                  <FormGroup>
                    <Label>Full Name *</Label>
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="John Doe"
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label>Email Address *</Label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="john@example.com"
                    />
                  </FormGroup>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                  <FormGroup>
                    <Label>Phone Number *</Label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      placeholder="+1 (555) 123-4567"
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label>Years of Experience *</Label>
                    <select
                      name="experience"
                      value={formData.experience}
                      onChange={handleInputChange}
                      required
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '2px solid #e2e8f0',
                        borderRadius: '8px',
                        fontSize: '16px',
                        background: 'white'
                      }}
                    >
                      <option value="">Select experience</option>
                      <option value="0-1">0-1 years</option>
                      <option value="1-3">1-3 years</option>
                      <option value="3-5">3-5 years</option>
                      <option value="5-10">5-10 years</option>
                      <option value="10+">10+ years</option>
                    </select>
                  </FormGroup>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                  <FormGroup>
                    <Label>Education Level *</Label>
                    <select
                      name="education"
                      value={formData.education}
                      onChange={handleInputChange}
                      required
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '2px solid #e2e8f0',
                        borderRadius: '8px',
                        fontSize: '16px',
                        background: 'white'
                      }}
                    >
                      <option value="">Select education</option>
                      <option value="High School">High School</option>
                      <option value="Bachelor's">Bachelor's Degree</option>
                      <option value="Master's">Master's Degree</option>
                      <option value="PhD">PhD</option>
                      <option value="Other">Other</option>
                    </select>
                  </FormGroup>
                  
                  <FormGroup>
                    <Label>Current Company</Label>
                    <Input
                      type="text"
                      name="currentCompany"
                      value={formData.currentCompany}
                      onChange={handleInputChange}
                      placeholder="Current employer (optional)"
                    />
                  </FormGroup>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                  <FormGroup>
                    <Label>Expected Salary</Label>
                    <Input
                      type="text"
                      name="expectedSalary"
                      value={formData.expectedSalary}
                      onChange={handleInputChange}
                      placeholder="e.g., $80,000 - $100,000"
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label>Available From *</Label>
                    <Input
                      type="date"
                      name="availableFrom"
                      value={formData.availableFrom}
                      onChange={handleInputChange}
                      required
                    />
                  </FormGroup>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                  <FormGroup>
                    <Label>Primary Technology *</Label>
                    <select
                      name="primaryTech"
                      value={formData.primaryTech}
                      onChange={handleInputChange}
                      required
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '2px solid #e2e8f0',
                        borderRadius: '8px',
                        fontSize: '16px',
                        background: 'white'
                      }}
                    >
                      <option value="">Select primary technology</option>
                      <option value="Frontend">Frontend Development</option>
                      <option value="Backend">Backend Development</option>
                      <option value="Fullstack">Full Stack Development</option>
                      <option value="Mobile">Mobile Development</option>
                      <option value="DevOps">DevOps/Cloud</option>
                      <option value="Data">Data Science/AI</option>
                      <option value="Design">UI/UX Design</option>
                      <option value="QA">Quality Assurance</option>
                    </select>
                  </FormGroup>
                  
                  <FormGroup>
                    <Label>Work Preference *</Label>
                    <select
                      name="workType"
                      value={formData.workType}
                      onChange={handleInputChange}
                      required
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '2px solid #e2e8f0',
                        borderRadius: '8px',
                        fontSize: '16px',
                        background: 'white'
                      }}
                    >
                      <option value="">Select work preference</option>
                      <option value="Remote">Remote Only</option>
                      <option value="Onsite">On-site Only</option>
                      <option value="Hybrid">Hybrid (Remote + On-site)</option>
                      <option value="Flexible">Flexible</option>
                    </select>
                  </FormGroup>
                </div>
                
                <FormGroup>
                  <Label>Required Skills for {selectedJob.title} *</Label>
                  <div style={{ 
                    border: '2px solid #e2e8f0', 
                    borderRadius: '12px', 
                    padding: '20px',
                    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                      <div style={{ background: '#0ea5e9', color: 'white', padding: '6px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' }}>
                        🏢 {getBranchInfo(selectedJob)} Role
                      </div>
                      <p style={{ color: '#64748b', fontSize: '14px', margin: '0' }}>Select your proficiency level in these key technologies:</p>
                    </div>
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', 
                      gap: '12px' 
                    }}>
                      {getJobSpecificSkills(selectedJob).map(skill => (
                        <label key={skill} style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '10px',
                          padding: '12px 16px',
                          background: formData.skills.includes(skill) ? 'linear-gradient(135deg, #fb923c 0%, #f97316 100%)' : 'white',
                          color: formData.skills.includes(skill) ? 'white' : '#374151',
                          borderRadius: '8px',
                          border: formData.skills.includes(skill) ? '2px solid #fb923c' : '2px solid #e5e7eb',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '500',
                          transition: 'all 0.3s ease',
                          boxShadow: formData.skills.includes(skill) ? '0 4px 12px rgba(251, 146, 60, 0.3)' : '0 2px 4px rgba(0, 0, 0, 0.05)'
                        }}>
                          <input
                            type="checkbox"
                            checked={formData.skills.includes(skill)}
                            onChange={() => handleSkillsChange(skill)}
                            style={{ display: 'none' }}
                          />
                          <div style={{ 
                            width: '18px', 
                            height: '18px', 
                            borderRadius: '4px', 
                            background: formData.skills.includes(skill) ? 'white' : 'transparent',
                            border: formData.skills.includes(skill) ? 'none' : '2px solid #d1d5db',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            color: '#fb923c'
                          }}>
                            {formData.skills.includes(skill) && '✓'}
                          </div>
                          {skill}
                        </label>
                      ))}
                    </div>
                    <div style={{ marginTop: '15px', padding: '12px', background: '#dbeafe', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
                      <p style={{ color: '#1e40af', fontSize: '13px', margin: '0' }}>
                        💡 <strong>Selected:</strong> {formData.skills.length} out of {getJobSpecificSkills(selectedJob).length} key skills for this {getBranchInfo(selectedJob)} position
                      </p>
                    </div>
                  </div>
                </FormGroup>
                
                <FormGroup>
                  <Label>Cover Letter *</Label>
                  <TextArea
                    name="coverLetter"
                    value={formData.coverLetter}
                    onChange={handleInputChange}
                    required
                    placeholder="Tell us why you're the perfect fit for this role. Highlight your relevant experience, skills, and what excites you about this opportunity..."
                    style={{ minHeight: '120px' }}
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label>Resume/CV *</Label>
                  <div style={{
                    border: uploadStatus === 'success' ? '2px solid #22c55e' : '2px dashed #cbd5e1',
                    borderRadius: '12px',
                    padding: '30px',
                    textAlign: 'center',
                    background: uploadStatus === 'success' ? '#f0fdf4' : '#f8fafc',
                    cursor: uploadStatus === 'uploading' ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease'
                  }}>
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={handleFileChange}
                      required
                      disabled={uploadStatus === 'uploading'}
                      style={{ display: 'none' }}
                      id="resume-upload"
                    />
                    <label htmlFor="resume-upload" style={{ cursor: uploadStatus === 'uploading' ? 'not-allowed' : 'pointer' }}>
                      {uploadStatus === 'idle' && (
                        <>
                          <div style={{ fontSize: '48px', marginBottom: '10px' }}>📄</div>
                          <p style={{ color: '#64748b', marginBottom: '5px' }}>Click to upload your resume</p>
                          <small style={{ color: '#94a3b8' }}>PDF, DOC, DOCX (Max 5MB)</small>
                        </>
                      )}
                      {uploadStatus === 'uploading' && (
                        <>
                          <div style={{ fontSize: '48px', marginBottom: '10px' }}>⏳</div>
                          <p style={{ color: '#fb923c', marginBottom: '5px', fontWeight: '600' }}>Uploading your resume...</p>
                          <div style={{ width: '200px', height: '6px', background: '#e5e7eb', borderRadius: '3px', margin: '10px auto', overflow: 'hidden' }}>
                            <div style={{ width: '100%', height: '100%', background: 'linear-gradient(90deg, #fb923c, #f97316)', borderRadius: '3px', animation: 'progress 1.5s ease-in-out' }}></div>
                          </div>
                          <small style={{ color: '#64748b' }}>Please wait...</small>
                        </>
                      )}
                      {uploadStatus === 'success' && formData.resume && (
                        <>
                          <div style={{ fontSize: '48px', marginBottom: '10px' }}>✅</div>
                          <p style={{ color: '#15803d', marginBottom: '5px', fontWeight: '600' }}>Resume uploaded successfully!</p>
                          <div style={{ background: 'white', padding: '10px', borderRadius: '8px', margin: '10px auto', maxWidth: '250px', border: '1px solid #bbf7d0' }}>
                            <p style={{ color: '#374151', fontSize: '14px', margin: '0', fontWeight: '500' }}>📎 {formData.resume.name}</p>
                            <p style={{ color: '#64748b', fontSize: '12px', margin: '5px 0 0 0' }}>{(formData.resume.size / 1024 / 1024).toFixed(2)} MB</p>
                          </div>
                          <small style={{ color: '#15803d' }}>Click to replace file</small>
                        </>
                      )}
                    </label>
                  </div>
                </FormGroup>
                
                <SubmitButton type="submit" style={{
                  background: 'linear-gradient(135deg, #fb923c 0%, #f97316 100%)',
                  boxShadow: '0 4px 15px rgba(251, 146, 60, 0.4)',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <span style={{ position: 'relative', zIndex: '2' }}>🚀 Submit Application to INITX</span>
                </SubmitButton>
                
                <div style={{ textAlign: 'center', marginTop: '20px', padding: '15px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <p style={{ color: '#64748b', fontSize: '12px', margin: '0', lineHeight: '1.5' }}>
                    🔒 <strong>Secure Application:</strong> Your information is encrypted and handled according to our privacy policy. 
                    We respect your privacy and will only use this information for recruitment purposes.
                  </p>
                </div>
              </form>
            </ModalContent>
          </ModalOverlay>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showSuccessModal && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowSuccessModal(false)}
          >
            <ModalContent
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                border: '3px solid #22c55e',
                boxShadow: '0 25px 50px rgba(34, 197, 94, 0.25)',
                textAlign: 'center',
                maxWidth: '500px'
              }}
            >
              <div style={{ position: 'absolute', top: '0', left: '0', right: '0', height: '6px', background: 'linear-gradient(90deg, #22c55e 0%, #16a34a 50%, #22c55e 100%)', borderRadius: '20px 20px 0 0' }}></div>
              
              <CloseButton onClick={() => setShowSuccessModal(false)}>
                <X size={24} />
              </CloseButton>
              
              <div style={{ padding: '20px 0' }}>
                <div style={{ fontSize: '64px', marginBottom: '20px' }}>✅</div>
                <h2 style={{ color: '#15803d', fontSize: '28px', fontWeight: '700', marginBottom: '10px' }}>Application Submitted Successfully!</h2>
                
                <div style={{ background: 'white', padding: '20px', borderRadius: '12px', margin: '20px 0', border: '2px solid #bbf7d0' }}>
                  <p style={{ color: '#374151', fontSize: '16px', marginBottom: '15px' }}>Your application has been received and is being processed.</p>
                  
                  <div style={{ background: '#f0fdf4', padding: '15px', borderRadius: '8px', marginBottom: '15px' }}>
                    <p style={{ color: '#15803d', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>📋 Application Details:</p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '13px' }}>
                      <div><strong>Application ID:</strong> {applicationId}</div>
                      <div><strong>Position:</strong> {selectedJob?.title}</div>
                      <div><strong>Department:</strong> {selectedJob?.department}</div>
                      <div><strong>Status:</strong> Under Review</div>
                    </div>
                  </div>
                  
                  <div style={{ background: '#dbeafe', padding: '12px', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
                    <p style={{ color: '#1e40af', fontSize: '13px', margin: '0' }}>
                      📧 <strong>Next Steps:</strong> You'll receive a confirmation email within 24 hours. Our HR team will contact you within 2-3 business days.
                    </p>
                  </div>
                </div>
                
                <button 
                  onClick={() => setShowSuccessModal(false)}
                  style={{
                    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                    color: 'white',
                    border: 'none',
                    padding: '12px 30px',
                    borderRadius: '25px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    boxShadow: '0 4px 15px rgba(34, 197, 94, 0.3)'
                  }}
                >
                  🎉 Continue Exploring Careers
                </button>
              </div>
            </ModalContent>
          </ModalOverlay>
        )}
      </AnimatePresence>

      <div style={{ 
        background: 'linear-gradient(135deg, #fb923c 0%, #f97316 100%)', 
        color: 'white', 
        padding: '60px 40px', 
        borderRadius: '20px', 
        textAlign: 'center',
        margin: '0 20px',
        maxWidth: '1200px',
        marginLeft: 'auto',
        marginRight: 'auto'
      }}>
        <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 2.5rem)', marginBottom: '20px' }}>Don't See Your Role?</h2>
        <p style={{ fontSize: '1.2rem', marginBottom: '30px', opacity: 0.9 }}>
          We're always looking for talented individuals. Send us your resume!
        </p>
        <button 
          style={{ 
            background: 'white', 
            color: '#f97316', 
            border: 'none', 
            padding: '15px 30px', 
            borderRadius: '25px', 
            fontWeight: '600',
            cursor: 'pointer',
            fontSize: '1.1rem'
          }}
          onClick={() => {
            const email = prompt('Enter your email to send resume:');
            if (email) {
              alert(`Resume submission feature coming soon! We'll contact you at ${email}`);
            }
          }}
        >
          Send Resume
        </button>
      </div>
    </CareersContainer>
  );
};

export default Careers;
